<template>
    <details :key="facet.field" v-if="showFacet(facet)" class="filter-section" v-bind="{'open': accordionState?.[facet.label]}" @toggle.prevent="toggleAccordion(facet.label, $event)">
        <summary class="filter-title">{{ facet.label }}</summary>

        <!-- GRID -->
        <div id="grid" v-if="facet.type === 'grid'" class="filter-content variants-palette">
            <a href="#" v-for="(value, index) in sortedFacetValues" :key="value.value" :class="['variant-item', (value.active ? 'variant-item-selected' : '')]" 
                :id="`${facet.field}_${value.value}`" @click="addOrRemoveFilter(facet, value, !value.active)" v-show="showFilterOptions(facet, index)">
                {{ value.label }}
            </a>
            <div v-if="showFacetValues" class="more-variants">
                <a @click="toggleAccordion(`${facet.label}_hiddenFilters`)">
                    {{ accordionState[`${facet.label}_hiddenFilters`] ? 'See Less' : `See ${facet.values.length - filtersToShowLimit[facet.type]} More`}}
                </a>
            </div>
        </div>

        <!-- PALETTE -->
        <div id="palette" v-else-if="facet.type === 'palette'" class="filter-content color-palette">
            <Cms model="color-camo-swatches" v-show="false" is-data driver="builder" v-model="colorSwatches" :searchParams="colorSwatchesSearchParams" id="cms_component" :limit="facet.values.length" />
            <a href="#" v-for="(value, index) in facet.values" :key="value.value" :class="['variant-item', (value.active ? 'variant-item-selected' : '')]"
                v-show="showFilterOptions(facet, index)"  
                :id="`${facet.field}_${value.value}`" @click="addOrRemoveFilter(facet, value, !value.active)">
                <img :src="colorSwatchesMap[value.label]" :alt="value.label">
                {{ value.label }}
            </a>
            <div v-if="showFacetValues" class="more-variants">
                <a @click="toggleAccordion(`${facet.label}_hiddenFilters`)">
                    {{ accordionState[`${facet.label}_hiddenFilters`] ? 'See Less' : `See ${facet.values.length - filtersToShowLimit[facet.type]} More`}}
                </a>
            </div>
        </div>

        <!-- SLIDER -->
        <div id="slider" v-else-if="facet.type === 'slider'" class="filter-content">
            <RothcoPriceSlider ref="slider"
                v-model:range="sliderVals"
                :min="basePriceRange[0]" :max="basePriceRange[1]"
            />
        </div> 

        <!-- RATING -->
        <form id="list" v-else-if="facet.field == 'rating'" class="filter-content rating-filter">
            <label v-for="(value) in facet.values" :key="value.value" :for="`${facet.field}_${value.low}`" :id="`${facet.field}_${value.low}_label`">
                <input type="checkbox" :id="`${facet.field}_${value.low}`" :checked="value.active" @click="addOrRemoveFilter(facet, value, !value.active)">
                <FiveStars v-if="value.low==5" alt="5 stars" />
                <FourStars v-else-if="value.low==4" alt="4 stars" />
                <ThreeStars v-else-if="value.low==3" alt="3 stars" />
                <TwoStars v-else-if="value.low==2" alt="2 stars"/>
                <OneStar v-else-if="value.low==1" alt="1 star" />
                <span v-if="value.low != 5" class="rating-text">& Up</span>
                <p>({{ value.count }})</p>
            </label>
        </form>

        <!-- LIST / HIERARCHY -->
        <form id="list" v-else class="filter-content">
            <div v-if="facet.type == 'hierarchy' && currentHierarchyFilter">
                <div @click="addOrRemoveFilter(currentHierarchyFilter.facet, currentHierarchyFilter.value, false)" class="hierarchy-1 hierarchy-label">
                    View All
                </div>
                <div v-for="(level, index) in currentHierarchyFilter.hierarchyLevels" :key="index" 
                    :class="[`hierarchy-${index+1}`, 'hierarchy-label', (currentHierarchyFilter.hierarchyLevels.length == (index+1) ? 'hierarchy-current' : '')]">
                    <span @click="addOrRemoveFilter(currentHierarchyFilter.facet,level.value,true)">
                        {{ contentFacet ? title(level.name) : level.name }}
                    </span>
                </div>
            </div>
            <label v-for="(value,index) in facet.values" :key="value.value" :for="`${facet.field}_${value.value}`" :id="`${facet.field}_${value.value}_label`"
                v-show="showFilterOptions(facet, index)" :class="[`hierarchy-${currentHierarchyFilter ? currentHierarchyFilter.hierarchyLevels.length : 1}`]">
                <input type="checkbox" :id="`${facet.field}_${value.value}`" :checked="value.active" aria-required="false" :aria-label="`Filter by ${value.label}`" aria-invalid="false" @click="addOrRemoveFilter(facet, value, !value.active)">
                {{ contentFacet ? title(value.label) : value.label }} <span>({{ value.count }})</span>
            </label>
            <div v-if="showFacetValues" class="more-variants">
                <a @click="toggleAccordion(`${facet.label}_hiddenFilters`)">
                    {{ accordionState[`${facet.label}_hiddenFilters`] ? 'See Less' : `See ${facet.values.length - filtersToShowLimit[facet.type]} More`}}
                </a>
            </div>
        </form>
    </details>
</template>

<script setup lang="ts">
    import { ref, computed, watch, toRefs, inject, type PropType} from "vue";
    import { type SearchSpringFacet } from "@interfaces/SearchSpringFacet";
    import RothcoPriceSlider from '@/components/RothcoPriceSlider.vue';
    import Cms from "@/components/Cms.vue";
    import { storeToRefs } from "pinia";
    import { useAccordionState } from "@/store/accordionState";
    import { useGenericStore } from "@/store/genericStore";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import FiveStars from "@icons/5_star.svg";
    import FourStars from "@icons/4_star.svg";
    import ThreeStars from "@icons/3_star.svg";
    import TwoStars from "@icons/2_star.svg";
    import OneStar from "@icons/1_star.svg";
    import {
        compareSearchSpringFacetSizeLabels,
        compareSearchSpringShoeWaistSizeLabels
    } from "@/helpers/sortSearchSpringSizeLabels";

    const emit = defineEmits(["addOrRemoveFilter"]);
    let props = defineProps({
        contentFacet: {
            type: Boolean,
            default: false
        },
        facet: {
            type: Object as PropType<SearchSpringFacet>,
            required: true,
            default() {
                return {
                    relatedActiveFilters: [],
                    values: []
                };
            }
        },
    });
    const { facet } = toRefs(props);
    let { optimize } = useImageOptimization();
    const colorSwatches = ref<{name: string, swatch: string}[]>([]);
    const colorSwatchesMap = computed(()=>{
        return colorSwatches.value?.reduce((acc,record)=>{
            return {
                ...acc,
                [record.name]: optimize(record.swatch)
            }
        }, {}) ?? {}
    })
    let swatchesStore;

    if(facet.value.type == 'palette'){
        swatchesStore = useGenericStore('colorSwatches', colorSwatches.value);
    }



    const colorSwatchesSearchParams = computed(()=>{
        if(facet.value.type == 'palette'){
            return {"data.name.$in": facet.value.values.map(value=>value.label)};
        }else{
            return null;
        }
    })

    function addOrRemoveFilter(facet, value, newVal) {
        let filter = {
            facet,
            value,
            active: newVal
        };
        emit("addOrRemoveFilter", filter);
    }

    let sortedFacetValues = computed(() => {
        const labelLower = facet.value.label.toLowerCase();

        if (
            labelLower.includes("size") &&
            (labelLower.includes("shoe") || labelLower.includes("waist"))
        ) {
            return [...facet.value.values].sort((a, b) =>
                compareSearchSpringShoeWaistSizeLabels(a.label, b.label)
            );
        }
        if (labelLower.includes("size")) {
            return [...facet.value.values].sort((a, b) =>
                compareSearchSpringFacetSizeLabels(a.label, b.label)
            );
        }
        return facet.value.values;
    });

    const { state:basePriceRange } = storeToRefs(useGenericStore("basePriceRange"));
    let slider = ref(null);
    if(facet.value.type == 'slider' && !basePriceRange.value){
        basePriceRange.value = [0, Math.ceil(facet.value.range[1])];
    }
    let sliderVals = ref(facet.value.range ?? basePriceRange.value);
    watch(sliderVals, (newVals)=>{
        if (Array.isArray(newVals)) {
            emit("addOrRemoveFilter", {
                facet: facet.value,
                value: {low: newVals[0], high: newVals[1]},
                active: true
            });
        }
    }, {deep: true})

    let currentHierarchyFilter = computed(() => {
        let obj = facet.value?.relatedActiveFilters?.[0];
        if (obj) {
            let hierarchyLevelsArr = obj.value.value.split(
                obj.facet.hierarchyDelimiter
            );
            return {
                ...obj,
                hierarchyLevels: hierarchyLevelsArr.map((level, index) => {
                    return {
                        name: level,
                        value: {
                            label: level,
                            value: hierarchyLevelsArr
                                .slice(0, index + 1)
                                .join(obj.facet.hierarchyDelimiter),
                        }
                    };
                })
            };
        }
        return null;
    });

    const { accordionState } = storeToRefs(useAccordionState());
    const { toggleAccordion } = useAccordionState();

    if(facet.value?.relatedActiveFilters?.length){
        toggleAccordion(facet.value.label, true)
    }

    const filtersToShowLimit = ref({
        'grid': 18,
        'palette': 20,
        'hierarchy': 8,
        'list': 8
    })

    const showFilterOptions = computed(()=>{
        return (facet, index)=>{
            let limit = filtersToShowLimit.value[facet.type] ?? 8;
            return accordionState.value?.[`${facet.label}_hiddenFilters`] ? true : index < limit;
        }
    })

    const showFacet = (facet)=>{
        return facet.field != 'product_id';
    }
    

    let showFacetValues = computed(()=>{
        if(Array.isArray(facet.value.values)){
            return facet.value.values.length > filtersToShowLimit.value[facet.value.type]
        }
        return false;
 })

 function title(name){
    return name.split('-').map((word)=>{
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
 }


 watch(colorSwatches, (newVal) => {
    if(facet.value.type == 'palette'){
        swatchesStore.set(newVal);
    }
}, { deep: true });
</script>